import { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import usePlacesAutocompleteService from 'react-google-autocomplete/lib/usePlacesAutocompleteService';
import { LocationData } from '../addressInput';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt, faTimes } from '@fortawesome/free-solid-svg-icons';
import { Colors } from '../../styles/colors';

const Container = styled.div<{ horizontal?: boolean }>`
  ${tw`flex flex-col relative`}
  ${props =>
    props.horizontal &&
    `
    gap: 4px;
    width: 100%;
    min-width: 0;
  `}
`;

const LocationLabel = styled.span`
  ${tw`text-sm font-medium mb-1`}
  color: ${Colors.TextSecondary};
`;

const InputWrapper = styled.div`
  ${tw`
    h-[44px]
    rounded-lg
    pl-3
    pr-3
    flex
    items-center
    cursor-text
    bg-white
  `}
  border: 1px solid ${Colors.BorderLight};
  &:hover {
    border-color: ${Colors.Obsidian};
  }
`;

const Icon = styled.span`
  color: ${Colors.Champagne};
  ${tw`
    fill-current
    text-xs
    md:text-base
  `};
`;

const StyledInput = styled.input<{ horizontal?: boolean }>`
  ${tw`
    flex-1
    outline-none
    text-base
    text-text-primary
    ml-2
  `}
  border: none;
  /* Keep >= 16px on mobile to avoid iOS Safari focus auto-zoom */
  font-size: 16px;
  ${props =>
    props.horizontal &&
    `
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `}
`;

const ClearButton = styled.button`
  ${tw`
    text-text-tertiary
    cursor-pointer
    ml-2
    p-1
    flex
    items-center
    justify-center
    transition-colors
  `}
  border: none;
  background: none;
  &:hover {
    ${tw`text-text-secondary`}
  }
`;

const SuggestionsContainer = styled.div`
  ${tw`
    absolute
    top-full
    left-0
    right-0
    bg-white
    shadow-card-hover
    rounded-md
    mt-1
    max-h-60
    overflow-y-auto
    z-[9999]
    border
    border-border-default
  `}
`;

const SuggestionItem = styled.div`
  ${tw`
    p-3
    cursor-pointer
    border-b
    border-border-default
    last:border-b-0
  `}
  &:hover {
    ${tw`bg-bg-secondary`}
  }
`;

const SuggestionMainText = styled.div`
  ${tw`
    text-sm
    md:text-base
    text-text-primary
    font-medium
  `}
`;

const SuggestionSecondaryText = styled.div`
  ${tw`
    text-xs
    md:text-sm
    text-text-secondary
    mt-1
  `}
`;

export interface AddressInputSectionProps {
  initialValue?: string;
  placeholder?: string;
  horizontal?: boolean;
  showLabel?: boolean;
  onLocationSelect: (location: LocationData, address: string) => void;
}

export function AddressInputSection({
  initialValue = '',
  placeholder = 'Enter an address...',
  horizontal = false,
  showLabel = false,
  onLocationSelect,
}: AddressInputSectionProps) {
  const [inputValue, setInputValue] = useState(initialValue);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  // Check if Google Maps API is loaded
  useEffect(() => {
    const checkGoogleMapsLoaded = () => {
      if (
        typeof window !== 'undefined' &&
        window.google &&
        window.google.maps &&
        window.google.maps.places
      ) {
        setIsGoogleMapsLoaded(true);
      } else {
        // Retry after a short delay if not loaded yet
        setTimeout(checkGoogleMapsLoaded, 100);
      }
    };
    checkGoogleMapsLoaded();
  }, []);

  const { placesService, placePredictions, getPlacePredictions, isPlacePredictionsLoading } =
    usePlacesAutocompleteService({
      apiKey: undefined, // API is loaded via script tag in index.html
      debounce: 500,
      options: {
        input: '',
      },
    });

  useEffect(() => {
    setInputValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    if (value.length > 2 && isGoogleMapsLoaded) {
      getPlacePredictions({ input: value });
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  const handleClear = () => {
    setInputValue('');
    setShowSuggestions(false);
  };

  const handlePredictionSelect = (prediction: google.maps.places.AutocompletePrediction) => {
    setInputValue(prediction.description);
    setShowSuggestions(false);

    // Blur on mobile so iOS does not keep a focus-zoom viewport
    if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
      inputRef.current?.blur();
    }

    if (placesService) {
      placesService.getDetails(
        {
          placeId: prediction.place_id,
          fields: [
            'address_components',
            'geometry.location',
            'formatted_address',
            'utc_offset_minutes',
          ],
        },
        (
          place: google.maps.places.PlaceResult | null,
          status: google.maps.places.PlacesServiceStatus,
        ) => {
          if (
            status === google.maps.places.PlacesServiceStatus.OK &&
            place &&
            place.geometry?.location
          ) {
            // Extract timezone
            const timezone = place.utc_offset_minutes
              ? `UTC${place.utc_offset_minutes >= 0 ? '+' : ''}${place.utc_offset_minutes / 60}`
              : 'America/Los_Angeles';

            // Extract zip code
            const zipCode =
              place.address_components?.find((component: google.maps.GeocoderAddressComponent) =>
                component.types.includes('postal_code'),
              )?.long_name || null;

            const locationData: LocationData = {
              address: place.formatted_address || '',
              latitude: place.geometry.location.lat(),
              longitude: place.geometry.location.lng(),
              formattedAddress: place.formatted_address || '',
              zipCode,
              timezone,
              currentLocalTimeISO: new Date().toISOString(),
            };

            onLocationSelect(locationData, place.formatted_address || '');
          }
        },
      );
    }
  };

  return (
    <Container horizontal={horizontal} ref={containerRef}>
      {showLabel && <LocationLabel>Enter address</LocationLabel>}
      <InputWrapper>
        <Icon>
          <FontAwesomeIcon icon={faMapMarkerAlt} />
        </Icon>
        <StyledInput
          ref={inputRef}
          placeholder={isGoogleMapsLoaded ? placeholder : 'Loading...'}
          horizontal={horizontal}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => inputValue.length > 0 && isGoogleMapsLoaded && setShowSuggestions(true)}
          disabled={!isGoogleMapsLoaded}
        />
        {inputValue && !isPlacePredictionsLoading && (
          <ClearButton onClick={handleClear} type="button">
            <FontAwesomeIcon icon={faTimes} />
          </ClearButton>
        )}
        {isPlacePredictionsLoading && (
          <div className="ml-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black"></div>
          </div>
        )}
      </InputWrapper>
      {showSuggestions && placePredictions.length > 0 && (
        <SuggestionsContainer>
          {placePredictions.map((prediction: google.maps.places.AutocompletePrediction) => (
            <SuggestionItem
              key={prediction.place_id}
              onClick={() => handlePredictionSelect(prediction)}
            >
              <SuggestionMainText>{prediction.structured_formatting.main_text}</SuggestionMainText>
              {prediction.structured_formatting.secondary_text && (
                <SuggestionSecondaryText>
                  {prediction.structured_formatting.secondary_text}
                </SuggestionSecondaryText>
              )}
            </SuggestionItem>
          ))}
        </SuggestionsContainer>
      )}
    </Container>
  );
}
